import React from 'react'
import { 
  Database, 
  Cloud, 
  CheckCircle2, 
  AlertTriangle,
  Zap,
  Building,
  CreditCard,
  Users,
  BarChart3,
  FileText,
  Calendar,
  Mail,
  ExternalLink,
  Shield,
  TrendingUp,
  Settings,
  Clock,
  Lightbulb
} from 'lucide-react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Checkbox } from '../../../components/ui/Checkbox'
import { Label } from '../../../components/ui/Label'
import { Button } from '../../../components/ui/Button'
import { HelpTooltip } from '../../../components/ui/Tooltip'
import { cn } from '../../../lib/utils'

interface IntegrationOption {
  id: string
  name: string
  description: string
  icon: React.ReactNode
  category: 'business' | 'financial' | 'marketing' | 'operational'
  difficulty: 'easy' | 'medium' | 'advanced'
  setupTime: string
  value: string
}

const integrationOptions: IntegrationOption[] = [
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    description: 'Financial data, revenue tracking, expense monitoring',
    icon: <Building className="w-5 h-5" />,
    category: 'financial',
    difficulty: 'easy',
    setupTime: '5 min',
    value: 'Real-time financial insights'
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Payment processing data, customer transactions',
    icon: <CreditCard className="w-5 h-5" />,
    category: 'financial',
    difficulty: 'easy', 
    setupTime: '3 min',
    value: 'Payment trend analysis'
  },
  {
    id: 'google-analytics',
    name: 'Google Analytics',
    description: 'Website traffic, customer behavior, conversion tracking',
    icon: <BarChart3 className="w-5 h-5" />,
    category: 'marketing',
    difficulty: 'medium',
    setupTime: '10 min',
    value: 'Digital marketing intelligence'
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Customer relationship data, sales pipeline, opportunities',
    icon: <Users className="w-5 h-5" />,
    category: 'business',
    difficulty: 'medium',
    setupTime: '15 min',
    value: 'Sales performance insights'
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    description: 'Spreadsheet data, custom business metrics, manual tracking',
    icon: <FileText className="w-5 h-5" />,
    category: 'operational',
    difficulty: 'easy',
    setupTime: '2 min',
    value: 'Custom data integration'
  },
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    description: 'Email marketing data, campaign performance, audience insights',
    icon: <Mail className="w-5 h-5" />,
    category: 'marketing',
    difficulty: 'easy',
    setupTime: '5 min',
    value: 'Marketing effectiveness tracking'
  },
  {
    id: 'calendar',
    name: 'Google Calendar',
    description: 'Appointment scheduling, business activity patterns',
    icon: <Calendar className="w-5 h-5" />,
    category: 'operational',
    difficulty: 'easy',
    setupTime: '3 min',
    value: 'Operational insights'
  },
  {
    id: 'api-webhook',
    name: 'Custom API/Webhook',
    description: 'Connect any system with API access or webhook capabilities',
    icon: <Cloud className="w-5 h-5" />,
    category: 'operational',
    difficulty: 'advanced',
    setupTime: '30+ min',
    value: 'Complete system integration'
  }
]

export const DataIntegrationScreen: React.FC = () => {
  const { data, updateData } = useOnboardingStore()

  const toggleIntegration = (integrationId: string) => {
    const currentIntegrations = data.dataIntegrations || []
    const updatedIntegrations = currentIntegrations.includes(integrationId)
      ? currentIntegrations.filter(id => id !== integrationId)
      : [...currentIntegrations, integrationId]
    
    updateData('dataIntegrations', updatedIntegrations)
  }

  const selectedIntegrations = data.dataIntegrations || []
  
  // Calculate stats
  const getStats = () => {
    const totalSelected = selectedIntegrations.length
    const categories = new Set(integrationOptions.filter(opt => selectedIntegrations.includes(opt.id)).map(opt => opt.category))
    const easySetups = selectedIntegrations.filter(id => {
      const option = integrationOptions.find(opt => opt.id === id)
      return option?.difficulty === 'easy'
    }).length
    
    return {
      totalSelected,
      categoriesCount: categories.size,
      easySetups,
      estimatedSetupTime: selectedIntegrations.reduce((total, id) => {
        const option = integrationOptions.find(opt => opt.id === id)
        const timeMatch = option?.setupTime.match(/\d+/)
        return total + (timeMatch ? parseInt(timeMatch[0]) : 0)
      }, 0)
    }
  }

  const stats = getStats()

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'financial': return CreditCard
      case 'marketing': return BarChart3
      case 'business': return Users
      case 'operational': return Settings
      default: return Database
    }
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-50 border-green-200'
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'advanced': return 'text-red-600 bg-red-50 border-red-200'
      default: return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-blue-100 rounded-full">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Database className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-blue-700">Data Integration</span>
        </div>
        
        <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
          Data Integration Setup
          <HelpTooltip text="Connect your business systems to enable comprehensive intelligence monitoring. The more data sources you connect, the more accurate and valuable your insights will be." />
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Connect your business systems to create a unified intelligence dashboard. 
          The more data you integrate, the more powerful and accurate your insights become.
        </p>

        {/* Progress Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{stats.totalSelected}</div>
            <div className="text-sm text-gray-600">Integrations</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-2xl font-bold text-green-600">{stats.categoriesCount}</div>
            <div className="text-sm text-gray-600">Categories</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-2xl font-bold text-purple-600">{stats.easySetups}</div>
            <div className="text-sm text-gray-600">Quick Setup</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-2xl font-bold text-orange-600">{stats.estimatedSetupTime}</div>
            <div className="text-sm text-gray-600">Minutes</div>
          </div>
        </div>
      </div>

      {/* Integration Options */}
      <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            Available Integrations
          </CardTitle>
          <CardDescription className="text-base">
            Select the systems you want to connect for comprehensive business intelligence
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {integrationOptions.map((integration) => {
              const isSelected = selectedIntegrations.includes(integration.id)
              const CategoryIcon = getCategoryIcon(integration.category)
              
              return (
                <Card 
                  key={integration.id}
                  className={cn(
                    "cursor-pointer transition-all duration-300 hover:shadow-lg border-2 relative overflow-hidden",
                    isSelected 
                      ? "border-blue-500 bg-blue-50 shadow-lg" 
                      : "border-gray-200 hover:border-blue-300"
                  )}
                  onClick={() => toggleIntegration(integration.id)}
                >
                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute top-4 right-4 z-10">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 fill-blue-100" />
                    </div>
                  )}

                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-12 h-12 rounded-xl flex items-center justify-center transition-colors",
                          isSelected 
                            ? "bg-blue-100 text-blue-600" 
                            : "bg-gray-100 text-gray-500"
                        )}>
                          {integration.icon}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-lg mb-2">{integration.name}</div>
                          <p className="text-sm text-gray-600 leading-relaxed">{integration.description}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 pt-2">
                        <div className={cn(
                          "px-2 py-1 rounded text-xs font-medium border",
                          getDifficultyColor(integration.difficulty)
                        )}>
                          {integration.difficulty}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Clock className="w-3 h-3" />
                          {integration.setupTime}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <CategoryIcon className="w-3 h-3" />
                          {integration.category}
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-xl p-3">
                        <div className="flex items-start gap-2">
                          <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Value:</strong> {integration.value}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      {selectedIntegrations.length > 0 && (
        <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              Next Steps: Integration Setup
            </CardTitle>
            <CardDescription className="text-base">
              After completing your configuration, you'll receive detailed setup instructions for each integration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedIntegrations.map((integrationId) => {
                const integration = integrationOptions.find(opt => opt.id === integrationId)
                if (!integration) return null
                
                return (
                  <div key={integrationId} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        {integration.icon}
                      </div>
                      <div>
                        <div className="font-semibold text-green-800 text-sm">
                          {integration.name}
                        </div>
                        <div className="text-xs text-green-700 mt-1">
                          Setup: {integration.setupTime} • {integration.difficulty}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-blue-800 mb-2">Security & Privacy</div>
                  <div className="text-blue-700 text-sm space-y-1">
                    <p>• All integrations use secure, industry-standard authentication</p>
                    <p>• Your data remains in your systems - we only read what's necessary</p>
                    <p>• You can disconnect any integration at any time</p>
                    <p>• We never share your data with third parties</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Intelligence Preview */}
      {selectedIntegrations.length > 0 && (
        <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-blue-800 mb-3">Integrated Intelligence Preview</h3>
              <p className="text-blue-700 mb-6">
                With {selectedIntegrations.length} integrations, our AI will provide comprehensive insights by connecting your:
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-blue-800">
                    <Database className="w-5 h-5" />
                    Data Sources
                  </div>
                  <ul className="space-y-2 text-sm text-blue-700 ml-7">
                    {selectedIntegrations.slice(0, 4).map((id) => {
                      const integration = integrationOptions.find(opt => opt.id === id)
                      return (
                        <li key={id}>• {integration?.name} - {integration?.value}</li>
                      )
                    })}
                    {selectedIntegrations.length > 4 && (
                      <li>• +{selectedIntegrations.length - 4} more integrations</li>
                    )}
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-blue-800">
                    <TrendingUp className="w-5 h-5" />
                    Intelligence Benefits
                  </div>
                  <ul className="space-y-2 text-sm text-blue-700 ml-7">
                    <li>• Unified dashboard with all your business metrics</li>
                    <li>• Cross-system insights and correlations</li>
                    <li>• Automated anomaly detection across data sources</li>
                    <li>• Predictive analytics based on integrated data</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-white/50 rounded-xl border border-blue-200/50">
                <div className="text-sm text-blue-700 font-medium">
                  🚀 Almost there! Configure intelligence delivery preferences next.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Status */}
      <div className={cn(
        "p-6 rounded-xl border-2 transition-all duration-300",
        stats.totalSelected >= 3 ? "bg-green-50 border-green-200" :
        stats.totalSelected >= 1 ? "bg-yellow-50 border-yellow-200" :
        "bg-gray-50 border-gray-200"
      )}>
        <div className="flex items-center gap-4">
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center",
            stats.totalSelected >= 3 ? "bg-green-100" :
            stats.totalSelected >= 1 ? "bg-yellow-100" :
            "bg-gray-100"
          )}>
            {stats.totalSelected >= 3 ? (
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            ) : (
              <Database className="w-6 h-6 text-gray-600" />
            )}
          </div>
          <div>
            <div className="text-lg font-semibold mb-1">
              Data Integration: {stats.totalSelected} sources selected
            </div>
            <div className="text-sm text-gray-600">
              {stats.totalSelected >= 3 && "Excellent integration coverage! "}
              {stats.totalSelected >= 1 && `${stats.categoriesCount} categories covered`}
              {stats.totalSelected === 0 && "Select at least one integration to get started"}
              {stats.estimatedSetupTime > 0 && ` • ~${stats.estimatedSetupTime} minutes total setup time`}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}